import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import {
  Search,
  Filter,
  MapPin,
  Calendar,
  DollarSign,
  Building2,
  Bookmark,
  BookmarkCheck,
  ExternalLink,
  Clock,
  Star,
  ChevronLeft,
  ChevronRight,
  FileText,
  Shield,
  TrendingUp,
  Loader2,
  Plus,
  List,
  LayoutGrid,
  RefreshCw,
  CheckCircle2,
  AlertCircle,
  Settings2,
} from "lucide-react";

import { API_BASE_URL as API } from "../lib/backendUrl";

const CATEGORIES = [
  { value: "construction", label: "Construction" },
  { value: "renovation", label: "Renovation" },
  { value: "infrastructure", label: "Infrastructure" },
  { value: "maintenance", label: "Maintenance" },
  { value: "design_build", label: "Design-Build" },
  { value: "professional_services", label: "Professional Services" },
];

const ORG_TYPES = [
  { value: "federal", label: "Federal", color: "bg-blue-500/20 text-blue-400" },
  { value: "state", label: "State", color: "bg-purple-500/20 text-purple-400" },
  { value: "local", label: "Local/Municipal", color: "bg-green-500/20 text-green-400" },
  { value: "private", label: "Private", color: "bg-amber-500/20 text-amber-400" },
];

const SET_ASIDES = [
  { value: "small_business", label: "Small Business" },
  { value: "8a", label: "8(a)" },
  { value: "hubzone", label: "HUBZone" },
  { value: "sdvosb", label: "SDVOSB" },
  { value: "wosb", label: "WOSB" },
  { value: "mbe", label: "MBE" },
  { value: "dbe", label: "DBE" },
];

const NOTICE_TYPES = [
  { value: "r", label: "Sources Sought" },
  { value: "p", label: "Presolicitation" },
  { value: "o", label: "Solicitation" },
  { value: "k", label: "Combined Synopsis" },
  { value: "s", label: "Special Notice" },
  { value: "a", label: "Award Notice" },
];

const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY", "DC"
];

const DiscoverPage = () => {
  const [opportunities, setOpportunities] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [savedItems, setSavedItems] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState({
    category: "",
    state: "",
    organization_type: "",
    source_type: "sam_gov",
    agency: "",
    notice_type: "",
    naics_code: "",
    psc_code: "",
    set_aside_type: "",
    response_deadline_to: "",
    saved: "",
    sort_by: "posted_date",
    sort_order: "desc",
    featured_only: false,
  });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedOpp, setSelectedOpp] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [viewMode, setViewMode] = useState("list"); // Default to list view
  const [resultMode, setResultMode] = useState("search");
  const [activeTab, setActiveTab] = useState("search");
  const [profiles, setProfiles] = useState([]);
  const [showProfileDialog, setShowProfileDialog] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  const emptyProfileForm = {
    name: "",
    description: "",
    is_default: false,
    enabled: true,
    keywords: "",
    negative_keywords: "",
    naics_codes: "",
    psc_codes: "",
    notice_types: "",
    set_aside_types: "",
    states: "",
    zip_codes: "",
    radius_miles: "",
    agency_names: "",
    min_response_days: "",
    max_response_days: "",
    min_estimated_value: "",
    max_estimated_value: "",
    risk_tolerance: "medium",
  };
  const [profileForm, setProfileForm] = useState(emptyProfileForm);
  const [selectedProfileId, setSelectedProfileId] = useState("");
  const [recomputing, setRecomputing] = useState(false);
  const [digest, setDigest] = useState(null);
  const [generatingDigest, setGeneratingDigest] = useState(false);
  
  // SAM.gov sync state
  const [samGovStatus, setSamGovStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [showSyncSettings, setShowSyncSettings] = useState(false);
  const [pruning, setPruning] = useState(false);

  useEffect(() => {
    fetchStats();
    fetchSamGovStatus();
    fetchProfiles();
    fetchSavedItems();
  }, []);

  useEffect(() => {
    fetchOpportunities();
  }, [page, filters, resultMode, selectedProfileId]);

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/discover/stats`);
      setStats(res.data);
    } catch (error) {
      console.error("Failed to load stats", error);
    }
  };

  const fetchSamGovStatus = async () => {
    try {
      const res = await axios.get(`${API}/discover/sam-gov/status`);
      setSamGovStatus(res.data);
    } catch (error) {
      console.error("Failed to load SAM.gov status", error);
    }
  };

  const fetchProfiles = async () => {
    try {
      const res = await axios.get(`${API}/discover/preference-profiles`);
      setProfiles(res.data.items || []);
    } catch (error) {
      console.error("Failed to load preference profiles", error);
    }
  };

  const fetchSavedItems = async () => {
    try {
      const res = await axios.get(`${API}/discover/saved`);
      setSavedItems(res.data || []);
    } catch (error) {
      console.error("Failed to load saved opportunities", error);
    }
  };

  const applyProfileToSearch = (profile) => {
    setFilters({
      ...filters,
      source_type: "sam_gov",
      state: profile.states?.[0] || "",
      notice_type: profile.notice_types?.[0] || "",
      naics_code: profile.naics_codes?.[0] || "",
      psc_code: profile.psc_codes?.[0] || "",
      set_aside_type: profile.set_aside_types?.[0] || "",
      agency: profile.agency_names?.[0] || "",
    });
    setActiveTab("search");
    setResultMode("search");
    setPage(1);
    toast.success(`Applied ${profile.name} filters`);
  };

  const splitList = (value) => value
    ? value.split(",").map(v => v.trim()).filter(Boolean)
    : [];

  const joinList = (value) => (value || []).join(", ");

  const openProfileDialog = (profile = null) => {
    setEditingProfile(profile);
    if (profile) {
      setProfileForm({
        name: profile.name || "",
        description: profile.description || "",
        is_default: !!profile.is_default,
        enabled: profile.enabled !== false,
        keywords: joinList(profile.keywords),
        negative_keywords: joinList(profile.negative_keywords),
        naics_codes: joinList(profile.naics_codes),
        psc_codes: joinList(profile.psc_codes),
        notice_types: joinList(profile.notice_types),
        set_aside_types: joinList(profile.set_aside_types),
        states: joinList(profile.states),
        zip_codes: joinList(profile.zip_codes),
        radius_miles: profile.radius_miles || "",
        agency_names: joinList(profile.agency_names),
        min_response_days: profile.min_response_days || "",
        max_response_days: profile.max_response_days || "",
        min_estimated_value: profile.min_estimated_value || "",
        max_estimated_value: profile.max_estimated_value || "",
        risk_tolerance: profile.risk_tolerance || "medium",
      });
    } else {
      setProfileForm(emptyProfileForm);
    }
    setShowProfileDialog(true);
  };

  const saveProfile = async () => {
    if (!profileForm.name.trim()) {
      toast.error("Profile name is required");
      return;
    }
    const payload = {
      name: profileForm.name.trim(),
      description: profileForm.description || null,
      is_default: profileForm.is_default,
      enabled: profileForm.enabled,
      keywords: splitList(profileForm.keywords),
      negative_keywords: splitList(profileForm.negative_keywords),
      naics_codes: splitList(profileForm.naics_codes),
      psc_codes: splitList(profileForm.psc_codes),
      notice_types: splitList(profileForm.notice_types),
      set_aside_types: splitList(profileForm.set_aside_types),
      states: splitList(profileForm.states).map(s => s.toUpperCase()),
      zip_codes: splitList(profileForm.zip_codes),
      radius_miles: profileForm.radius_miles ? Number(profileForm.radius_miles) : null,
      agency_names: splitList(profileForm.agency_names),
      min_response_days: profileForm.min_response_days ? Number(profileForm.min_response_days) : null,
      max_response_days: profileForm.max_response_days ? Number(profileForm.max_response_days) : null,
      min_estimated_value: profileForm.min_estimated_value ? Number(profileForm.min_estimated_value) : null,
      max_estimated_value: profileForm.max_estimated_value ? Number(profileForm.max_estimated_value) : null,
      risk_tolerance: profileForm.risk_tolerance || "medium",
    };
    try {
      if (editingProfile) {
        await axios.put(`${API}/discover/preference-profiles/${editingProfile.id}`, payload);
        toast.success("Preference profile updated");
      } else {
        await axios.post(`${API}/discover/preference-profiles`, payload);
        toast.success("Preference profile created");
      }
      setShowProfileDialog(false);
      fetchProfiles();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save profile");
    }
  };

  const deleteProfile = async (profile) => {
    if (!window.confirm(`Delete preference profile \"${profile.name}\"?`)) return;
    try {
      await axios.delete(`${API}/discover/preference-profiles/${profile.id}`);
      toast.success("Preference profile deleted");
      fetchProfiles();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete profile");
    }
  };

  const handleSamGovPrune = async () => {
    setPruning(true);
    try {
      const res = await axios.post(`${API}/discover/sam-gov/prune?stale_days=45`);
      toast.success(`Marked ${res.data.marked_inactive} stale/expired opportunities inactive`);
      fetchOpportunities();
      fetchStats();
      fetchSamGovStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to prune SAM.gov opportunities");
    } finally {
      setPruning(false);
      setShowSyncSettings(false);
    }
  };

  const handleSamGovSync = async (daysBack = 30) => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/discover/sam-gov/sync?days_back=${daysBack}`);
      toast.success(`Synced ${res.data.created + res.data.updated} opportunities from SAM.gov`);
      fetchOpportunities();
      fetchStats();
      fetchSamGovStatus();
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to sync with SAM.gov";
      toast.error(message);
    } finally {
      setSyncing(false);
      setShowSyncSettings(false);
    }
  };

  const fetchOpportunities = async () => {
    setLoading(true);
    try {
      if (resultMode === "recommended") {
        const params = new URLSearchParams();
        params.append("page", page.toString());
        params.append("page_size", "12");
        if (selectedProfileId && selectedProfileId !== "default") params.append("profile_id", selectedProfileId);
        const res = await axios.get(`${API}/discover/recommendations?${params}`);
        setRecommendations(res.data.items || []);
        setOpportunities(res.data.items || []);
        setTotalPages(res.data.total_pages || 1);
        return;
      }

      const params = new URLSearchParams();
      params.append("page", page.toString());
      params.append("page_size", "12");
      
      if (searchQuery) params.append("search", searchQuery);
      if (filters.category && filters.category !== "all") params.append("category", filters.category);
      if (filters.state && filters.state !== "all") params.append("state", filters.state);
      if (filters.organization_type && filters.organization_type !== "all") params.append("organization_type", filters.organization_type);
      if (filters.source_type && filters.source_type !== "all") params.append("source_type", filters.source_type);
      if (filters.agency) params.append("agency", filters.agency);
      if (filters.notice_type && filters.notice_type !== "all") params.append("notice_type", filters.notice_type);
      if (filters.naics_code) params.append("naics_code", filters.naics_code.trim());
      if (filters.psc_code) params.append("psc_code", filters.psc_code.trim());
      if (filters.set_aside_type && filters.set_aside_type !== "all") params.append("set_aside_type", filters.set_aside_type);
      if (filters.response_deadline_to) params.append("response_deadline_to", filters.response_deadline_to);
      if (filters.saved === "saved") params.append("saved", "true");
      if (filters.saved === "unsaved") params.append("saved", "false");
      if (filters.sort_by) params.append("sort_by", filters.sort_by);
      if (filters.sort_order) params.append("sort_order", filters.sort_order);
      if (filters.featured_only) params.append("featured_only", "true");

      const res = await axios.get(`${API}/discover/opportunities?${params}`);
      setOpportunities(res.data.items);
      setTotalPages(res.data.total_pages);
    } catch (error) {
      toast.error("Failed to load opportunities");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setResultMode("search");
    setPage(1);
    fetchOpportunities();
  };

  const recomputeMatches = async () => {
    const profileId = selectedProfileId || profiles.find(p => p.is_default)?.id || profiles[0]?.id;
    if (!profileId) {
      toast.error("Create a preference profile first");
      return;
    }
    setRecomputing(true);
    try {
      const res = await axios.post(`${API}/discover/preference-profiles/${profileId}/recompute-matches`);
      toast.success(`Scored ${res.data.evaluated} opportunities`);
      setSelectedProfileId(profileId);
      setResultMode("recommended");
      setPage(1);
      fetchOpportunities();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to recompute matches");
    } finally {
      setRecomputing(false);
    }
  };

  const handleSave = async (opp) => {
    setSaving(true);
    try {
      if (opp.saved) {
        await axios.delete(`${API}/discover/opportunities/${opp.id}/save`);
        toast.success("Removed from saved");
      } else {
        await axios.post(`${API}/discover/opportunities/${opp.id}/save`, {});
        toast.success("Saved to your list");
      }
      fetchOpportunities();
      if (selectedOpp?.id === opp.id) {
        const res = await axios.get(`${API}/discover/opportunities/${opp.id}`);
        setSelectedOpp(res.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update");
    } finally {
      setSaving(false);
    }
  };

  const refreshSelectedOpportunity = async (id) => {
    const res = await axios.get(`${API}/discover/opportunities/${id}`);
    setSelectedOpp(res.data);
  };

  const openDetail = async (opp) => {
    try {
      await refreshSelectedOpportunity(opp.id);
      setIsDetailOpen(true);
    } catch (error) {
      toast.error("Failed to load opportunity details");
    }
  };

  const setPursuitStatus = async (status) => {
    if (!selectedOpp) return;
    try {
      await axios.post(`${API}/discover/opportunities/${selectedOpp.id}/pursuit`, { status });
      toast.success(`Marked as ${status.replace("_", " ")}`);
      await refreshSelectedOpportunity(selectedOpp.id);
      fetchOpportunities();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update pursuit status");
    }
  };

  const generateDigest = async () => {
    setGeneratingDigest(true);
    try {
      const res = await axios.post(`${API}/discover/digest`, {
        profile_id: selectedProfileId || null,
        min_score: 50,
        deadline_days: 14,
        limit: 8,
      });
      setDigest(res.data);
      toast.success("Opportunity digest generated");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate digest");
    } finally {
      setGeneratingDigest(false);
    }
  };

  const convertToCrmOpportunity = async () => {
    if (!selectedOpp) return;
    try {
      const res = await axios.post(`${API}/discover/opportunities/${selectedOpp.id}/convert/crm-opportunity`, {
        stage: "identified",
        probability_percent: 10,
      });
      toast.success(res.data.already_exists ? "CRM opportunity already exists" : "Converted to CRM opportunity");
      await refreshSelectedOpportunity(selectedOpp.id);
      fetchOpportunities();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to convert opportunity");
    }
  };

  const formatCurrency = (min, max) => {
    const format = (v) => {
      if (!v) return null;
      if (v >= 1000000) return `$${(v / 1000000).toFixed(1)}M`;
      if (v >= 1000) return `$${(v / 1000).toFixed(0)}K`;
      return `$${v.toLocaleString()}`;
    };
    
    const minStr = format(min);
    const maxStr = format(max);
    
    if (minStr && maxStr && minStr !== maxStr) return `${minStr} - ${maxStr}`;
    return maxStr || minStr || "Not specified";
  };

  const formatDeadline = (deadline) => {
    if (!deadline) return null;
    const date = new Date(deadline);
    const now = new Date();
    const diff = Math.ceil((date - now) / (1000 * 60 * 60 * 24));
    
    if (diff < 0) return { text: "Expired", urgent: true };
    if (diff === 0) return { text: "Today", urgent: true };
    if (diff === 1) return { text: "Tomorrow", urgent: true };
    if (diff <= 7) return { text: `${diff} days left`, urgent: true };
    return { text: date.toLocaleDateString(), urgent: false };
  };

  const getOrgTypeBadge = (type) => {
    const org = ORG_TYPES.find(o => o.value === type);
    return org || { label: type, color: "bg-muted text-muted-foreground" };
  };

  return (
    <div className="p-6 space-y-6" data-testid="discover-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-heading">Discover Opportunities</h1>
          <p className="text-muted-foreground">Find and track RFPs from government and private sources</p>
        </div>
        
        {/* SAM.gov Sync Controls */}
        <div className="flex items-center gap-3">
          {samGovStatus && (
            <div className="flex items-center gap-2 text-sm">
              {samGovStatus.is_configured ? (
                <div className="flex items-center gap-1.5 text-green-400">
                  <CheckCircle2 className="h-4 w-4" />
                  <span>SAM.gov Connected</span>
                </div>
              ) : (
                <div className="flex items-center gap-1.5 text-amber-400">
                  <AlertCircle className="h-4 w-4" />
                  <span>SAM.gov Not Configured</span>
                </div>
              )}
              {samGovStatus.last_sync_at && (
                <span className="text-muted-foreground text-xs ml-2">
                  Last sync: {new Date(samGovStatus.last_sync_at).toLocaleDateString()}
                </span>
              )}
              {samGovStatus.latest_run && (
                <span className="text-muted-foreground text-xs ml-2">
                  Latest run: {samGovStatus.latest_run.status} · {samGovStatus.latest_run.records_received || 0} received
                </span>
              )}
            </div>
          )}
          
          <div className="relative">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowSyncSettings(!showSyncSettings)}
              disabled={syncing || !samGovStatus?.is_configured}
              data-testid="sam-gov-sync-btn"
            >
              {syncing ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              Sync SAM.gov
            </Button>
            
            {showSyncSettings && (
              <div className="absolute right-0 top-full mt-2 w-64 bg-card border rounded-lg shadow-lg p-4 z-50">
                <h4 className="font-medium mb-3">Sync Settings</h4>
                <p className="text-sm text-muted-foreground mb-3">
                  Pull construction opportunities from SAM.gov
                </p>
                <div className="space-y-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => handleSamGovSync(7)}
                    disabled={syncing}
                  >
                    <Calendar className="h-4 w-4 mr-2" />
                    Last 7 days
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => handleSamGovSync(30)}
                    disabled={syncing}
                  >
                    <Calendar className="h-4 w-4 mr-2" />
                    Last 30 days
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => handleSamGovSync(90)}
                    disabled={syncing}
                  >
                    <Calendar className="h-4 w-4 mr-2" />
                    Last 90 days
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full justify-start"
                    onClick={handleSamGovPrune}
                    disabled={pruning}
                  >
                    {pruning ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Filter className="h-4 w-4 mr-2" />}
                    Prune expired/stale
                  </Button>
                </div>
                {!samGovStatus?.is_configured && (
                  <p className="text-xs text-amber-400 mt-3">
                    Add SAM_GOV_API_KEY to enable sync
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="stat-card">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Active Opportunities</p>
                <p className="text-2xl font-bold">{stats.total_active || 0}</p>
              </div>
              <FileText className="h-8 w-8 text-primary/50" />
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Deadline This Week</p>
                <p className="text-2xl font-bold text-amber-400">{stats.deadline_within_7_days || 0}</p>
              </div>
              <Clock className="h-8 w-8 text-amber-500/50" />
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Featured</p>
                <p className="text-2xl font-bold">{stats.featured || 0}</p>
              </div>
              <Star className="h-8 w-8 text-yellow-500/50" />
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Saved</p>
                <p className="text-2xl font-bold text-green-400">{stats.saved || 0}</p>
              </div>
              <BookmarkCheck className="h-8 w-8 text-green-500/50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Workflow Tabs */}
      <div className="flex flex-wrap gap-2 border rounded-xl p-1 bg-muted/20">
        {[
          ["search", "Search"],
          ["recommended", "Recommended"],
          ["pursuits", "Saved / Pursuits"],
          ["profiles", "Profiles"],
          ["ops", "Ops"],
        ].map(([key, label]) => (
          <Button
            key={key}
            variant={activeTab === key ? "default" : "ghost"}
            size="sm"
            onClick={() => {
              setActiveTab(key);
              if (key === "recommended") setResultMode("recommended");
              if (key === "search") setResultMode("search");
              if (key === "pursuits") fetchSavedItems();
              setPage(1);
            }}
          >
            {label}
          </Button>
        ))}
      </div>

      {/* Preference Profiles */}
      {activeTab === "profiles" && <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Settings2 className="h-5 w-5" />
              Opportunity Preference Profiles
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Define the work types, agencies, locations, NAICS/PSC codes, and deadlines this tenant cares about.
            </p>
          </div>
          <Button onClick={() => openProfileDialog()}>
            <Plus className="h-4 w-4 mr-2" />
            New Profile
          </Button>
        </CardHeader>
        <CardContent>
          {profiles.length === 0 ? (
            <div className="text-sm text-muted-foreground border rounded-lg p-4">
              No preference profiles yet. Create one to prepare for recommended opportunities in Phase 5.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {profiles.map(profile => (
                <div key={profile.id} className="border rounded-lg p-3 space-y-2 bg-muted/20">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{profile.name}</p>
                        {profile.is_default && <Badge variant="outline" className="text-xs bg-primary/15 text-primary">Default</Badge>}
                        {!profile.enabled && <Badge variant="outline" className="text-xs">Disabled</Badge>}
                      </div>
                      {profile.description && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{profile.description}</p>}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {(profile.states || []).slice(0, 4).map(s => <Badge key={s} variant="outline" className="text-xs">{s}</Badge>)}
                    {(profile.naics_codes || []).slice(0, 3).map(n => <Badge key={n} variant="outline" className="text-xs">NAICS {n}</Badge>)}
                    {(profile.keywords || []).slice(0, 3).map(k => <Badge key={k} variant="outline" className="text-xs">{k}</Badge>)}
                  </div>
                  <div className="flex gap-2 pt-1 flex-wrap">
                    <Button size="sm" variant="outline" onClick={() => applyProfileToSearch(profile)}>Apply</Button>
                    <Button size="sm" variant="outline" onClick={() => openProfileDialog(profile)}>Edit</Button>
                    <Button size="sm" variant="ghost" onClick={() => deleteProfile(profile)}>Delete</Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>}

      {/* Ops Panel */}
      {activeTab === "ops" && (
        <Card>
          <CardHeader>
            <CardTitle>SAM.gov Operations</CardTitle>
            <p className="text-sm text-muted-foreground">Run manual syncs, prune stale records, and review current integration state.</p>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="text-sm">
              <p>Status: {samGovStatus?.is_configured ? "Connected" : "Not configured"}</p>
              <p>Last sync: {samGovStatus?.last_sync_at ? new Date(samGovStatus.last_sync_at).toLocaleString() : "Never"}</p>
              <p>Latest run: {samGovStatus?.latest_run ? `${samGovStatus.latest_run.status} · ${samGovStatus.latest_run.records_received || 0} received` : "None"}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => handleSamGovSync(7)} disabled={syncing}>Sync 7 days</Button>
              <Button variant="outline" onClick={() => handleSamGovSync(30)} disabled={syncing}>Sync 30 days</Button>
              <Button variant="outline" onClick={() => handleSamGovSync(90)} disabled={syncing}>Sync 90 days</Button>
              <Button variant="outline" onClick={handleSamGovPrune} disabled={pruning}>Prune stale/expired</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search & Filters */}
      {(activeTab === "search" || activeTab === "recommended") && <div className="flex gap-4 flex-wrap">
        <div className="flex items-center border rounded-lg overflow-hidden">
          <Button variant={resultMode === "search" ? "default" : "ghost"} className="rounded-none" onClick={() => { setResultMode("search"); setPage(1); }}>
            Search
          </Button>
          <Button variant={resultMode === "recommended" ? "default" : "ghost"} className="rounded-none" onClick={() => { setResultMode("recommended"); setPage(1); }}>
            Recommended
          </Button>
        </div>

        <Select value={selectedProfileId || "default"} onValueChange={(v) => { setSelectedProfileId(v === "default" ? "" : v); setPage(1); }}>
          <SelectTrigger className="w-48 bg-muted/50">
            <SelectValue placeholder="Profile" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="default">Default Profile</SelectItem>
            {profiles.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>

        <Button variant="outline" onClick={recomputeMatches} disabled={recomputing || profiles.length === 0}>
          {recomputing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <TrendingUp className="h-4 w-4 mr-2" />}
          Score Matches
        </Button>

        <Button variant="outline" onClick={generateDigest} disabled={generatingDigest}>
          {generatingDigest ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileText className="h-4 w-4 mr-2" />}
          Digest
        </Button>
        <form onSubmit={handleSearch} className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by title, organization, solicitation #..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-muted/50"
            data-testid="discover-search"
          />
        </form>

        <Select value={filters.category || "all"} onValueChange={(v) => { setFilters({...filters, category: v === "all" ? "" : v}); setPage(1); }}>
          <SelectTrigger className="w-40 bg-muted/50">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {CATEGORIES.map(c => (
              <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={filters.state || "all"} onValueChange={(v) => { setFilters({...filters, state: v === "all" ? "" : v}); setPage(1); }}>
          <SelectTrigger className="w-32 bg-muted/50">
            <SelectValue placeholder="State" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All States</SelectItem>
            {US_STATES.map(s => (
              <SelectItem key={s} value={s}>{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={filters.organization_type || "all"} onValueChange={(v) => { setFilters({...filters, organization_type: v === "all" ? "" : v}); setPage(1); }}>
          <SelectTrigger className="w-36 bg-muted/50">
            <SelectValue placeholder="Org Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {ORG_TYPES.map(o => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={filters.set_aside_type || "all"} onValueChange={(v) => { setFilters({...filters, set_aside_type: v === "all" ? "" : v}); setPage(1); }}>
          <SelectTrigger className="w-36 bg-muted/50">
            <SelectValue placeholder="Set-Aside" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Set-Asides</SelectItem>
            {SET_ASIDES.map(s => (
              <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={filters.notice_type || "all"} onValueChange={(v) => { setFilters({...filters, notice_type: v === "all" ? "" : v}); setPage(1); }}>
          <SelectTrigger className="w-44 bg-muted/50">
            <SelectValue placeholder="Notice Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Notice Types</SelectItem>
            {NOTICE_TYPES.map(n => (
              <SelectItem key={n.value} value={n.value}>{n.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Input
          placeholder="Agency"
          value={filters.agency}
          onChange={(e) => setFilters({...filters, agency: e.target.value})}
          onBlur={() => setPage(1)}
          className="w-40 bg-muted/50"
        />

        <Input
          placeholder="NAICS"
          value={filters.naics_code}
          onChange={(e) => setFilters({...filters, naics_code: e.target.value})}
          onBlur={() => setPage(1)}
          className="w-28 bg-muted/50"
        />

        <Input
          placeholder="PSC"
          value={filters.psc_code}
          onChange={(e) => setFilters({...filters, psc_code: e.target.value})}
          onBlur={() => setPage(1)}
          className="w-24 bg-muted/50"
        />

        <Input
          type="date"
          value={filters.response_deadline_to}
          onChange={(e) => { setFilters({...filters, response_deadline_to: e.target.value}); setPage(1); }}
          className="w-40 bg-muted/50"
          title="Response deadline before"
        />

        <Select value={filters.saved || "all"} onValueChange={(v) => { setFilters({...filters, saved: v === "all" ? "" : v}); setPage(1); }}>
          <SelectTrigger className="w-36 bg-muted/50">
            <SelectValue placeholder="Saved" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Items</SelectItem>
            <SelectItem value="saved">Saved</SelectItem>
            <SelectItem value="unsaved">Unsaved</SelectItem>
          </SelectContent>
        </Select>

        <Select value={filters.sort_by || "posted_date"} onValueChange={(v) => { setFilters({...filters, sort_by: v}); setPage(1); }}>
          <SelectTrigger className="w-40 bg-muted/50">
            <SelectValue placeholder="Sort" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="posted_date">Newest Posted</SelectItem>
            <SelectItem value="deadline">Deadline</SelectItem>
            <SelectItem value="agency">Agency</SelectItem>
            <SelectItem value="title">Title</SelectItem>
            <SelectItem value="last_synced_at">Last Synced</SelectItem>
          </SelectContent>
        </Select>

        <Button 
          variant={filters.featured_only ? "default" : "outline"}
          onClick={() => { setFilters({...filters, featured_only: !filters.featured_only}); setPage(1); }}
        >
          <Star className={`h-4 w-4 mr-1 ${filters.featured_only ? "fill-current" : ""}`} />
          Featured
        </Button>

        {/* View Toggle */}
        <div className="flex items-center border rounded-lg overflow-hidden ml-auto">
          <Button
            variant={viewMode === "list" ? "default" : "ghost"}
            size="sm"
            className="rounded-none"
            onClick={() => setViewMode("list")}
            data-testid="view-list-btn"
          >
            <List className="h-4 w-4" />
          </Button>
          <Button
            variant={viewMode === "grid" ? "default" : "ghost"}
            size="sm"
            className="rounded-none"
            onClick={() => setViewMode("grid")}
            data-testid="view-grid-btn"
          >
            <LayoutGrid className="h-4 w-4" />
          </Button>
        </div>
      </div>}

      {(activeTab === "search" || activeTab === "recommended") && digest && (
        <Card className="border-primary/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Opportunity Digest
            </CardTitle>
            <p className="text-sm text-muted-foreground">{digest.headline}</p>
          </CardHeader>
          <CardContent className="space-y-4">
            {digest.deadline_alerts?.length > 0 && (
              <div>
                <h4 className="font-medium mb-2">Upcoming Pursuit Deadlines</h4>
                <div className="space-y-2">
                  {digest.deadline_alerts.map((item, idx) => (
                    <div key={idx} className="text-sm border rounded p-2 flex justify-between gap-3">
                      <span>{item.opportunity.title}</span>
                      <span className="text-red-300">{item.days_left} days left</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {digest.top_recommendations?.length > 0 && (
              <div>
                <h4 className="font-medium mb-2">Top Recommendations</h4>
                <div className="space-y-2">
                  {digest.top_recommendations.slice(0, 5).map(item => (
                    <div key={item.id} className="text-sm border rounded p-2">
                      <div className="flex justify-between gap-3">
                        <span className="font-medium">{item.title}</span>
                        <Badge variant="outline" className="bg-green-500/15 text-green-300">{item.match_score}%</Badge>
                      </div>
                      <p className="text-muted-foreground mt-1">{item.intelligence?.recommendation}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div>
              <h4 className="font-medium mb-2">Next Actions</h4>
              <ul className="list-disc pl-5 text-sm text-muted-foreground">
                {digest.next_actions?.map((a, i) => <li key={i}>{a}</li>)}
              </ul>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Saved / Pursuits Board */}
      {activeTab === "pursuits" && (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {["reviewing", "pursuing", "preparing", "submitted"].map(status => {
            const items = savedItems.filter(item => (item.saved_info?.application_status || "saved") === status);
            return (
              <Card key={status}>
                <CardHeader>
                  <CardTitle className="capitalize text-base">{status.replace("_", " ")} ({items.length})</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {items.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No items</p>
                  ) : items.map(item => (
                    <div key={item.id} className="border rounded-lg p-3 space-y-2 cursor-pointer hover:bg-muted/30" onClick={() => openDetail(item)}>
                      <p className="font-medium text-sm line-clamp-2">{item.title}</p>
                      <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                        <span>{item.organization_name || "Unknown"}</span>
                        {item.response_deadline && <span>{formatDeadline(item.response_deadline)?.text}</span>}
                      </div>
                      {item.saved_info?.priority && <Badge variant="outline" className="text-xs">{item.saved_info.priority}</Badge>}
                    </div>
                  ))}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Opportunities Display */}
      {(activeTab === "search" || activeTab === "recommended") && (loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : opportunities.length === 0 ? (
        <div className="text-center py-16">
          <FileText className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium mb-2">No opportunities found</h3>
          <p className="text-muted-foreground">Try adjusting your filters or search query</p>
        </div>
      ) : viewMode === "list" ? (
        /* List View */
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr className="text-left text-sm text-muted-foreground">
                <th className="p-3 font-medium">Title / Organization</th>
                <th className="p-3 font-medium hidden md:table-cell">Category</th>
                <th className="p-3 font-medium hidden lg:table-cell">Location</th>
                <th className="p-3 font-medium">Value</th>
                <th className="p-3 font-medium hidden md:table-cell">Due Date</th>
                <th className="p-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {opportunities.map((opp) => {
                const deadline = formatDeadline(opp.response_deadline);
                const orgBadge = getOrgTypeBadge(opp.organization_type);
                
                return (
                  <tr 
                    key={opp.id} 
                    className="hover:bg-muted/30 cursor-pointer transition-colors"
                    onClick={() => openDetail(opp)}
                    data-testid={`opportunity-row-${opp.id}`}
                  >
                    <td className="p-3">
                      <div className="flex items-start gap-2">
                        {opp.is_featured && (
                          <Star className="h-4 w-4 text-yellow-500 fill-yellow-500 flex-shrink-0 mt-0.5" />
                        )}
                        <div className="min-w-0">
                          <p className="font-medium line-clamp-1">{opp.title}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-sm text-muted-foreground flex items-center gap-1">
                              <Building2 className="h-3 w-3" />
                              <span className="truncate max-w-[200px]">{opp.organization_name || "Unknown"}</span>
                            </span>
                            <Badge variant="outline" className={`text-xs ${orgBadge.color}`}>
                              {orgBadge.label}
                            </Badge>
                            {opp.source_type === "sam_gov" && (
                              <Badge variant="outline" className="text-xs bg-blue-500/15 text-blue-300">
                                SAM.gov
                              </Badge>
                            )}
                            {(opp.sam_notice_type_label || opp.solicitation_type) && (
                              <Badge variant="outline" className="text-xs">
                                {opp.sam_notice_type_label || opp.solicitation_type}
                              </Badge>
                            )}
                            {opp.match_score !== undefined && (
                              <Badge variant="outline" className="text-xs bg-green-500/15 text-green-300">
                                {opp.match_score}% match
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="p-3 hidden md:table-cell">
                      {opp.category ? (
                        <Badge variant="outline" className="capitalize text-xs">
                          {opp.category.replace("_", " ")}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground text-sm">-</span>
                      )}
                    </td>
                    <td className="p-3 hidden lg:table-cell">
                      <span className="text-sm flex items-center gap-1 text-muted-foreground">
                        <MapPin className="h-3 w-3" />
                        {opp.city && opp.state ? `${opp.city}, ${opp.state}` : opp.state || "N/A"}
                      </span>
                    </td>
                    <td className="p-3">
                      <span className="font-medium text-primary text-sm">
                        {formatCurrency(opp.estimated_value_min, opp.estimated_value_max)}
                      </span>
                    </td>
                    <td className="p-3 hidden md:table-cell">
                      {deadline ? (
                        <span className={`text-sm flex items-center gap-1 ${deadline.urgent ? "text-red-400" : "text-muted-foreground"}`}>
                          <Calendar className="h-3 w-3" />
                          {deadline.text}
                        </span>
                      ) : (
                        <span className="text-muted-foreground text-sm">-</span>
                      )}
                    </td>
                    <td className="p-3 text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => { e.stopPropagation(); handleSave(opp); }}
                        disabled={saving}
                      >
                        {opp.saved ? (
                          <BookmarkCheck className="h-4 w-4 text-primary" />
                        ) : (
                          <Bookmark className="h-4 w-4" />
                        )}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        /* Grid View */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {opportunities.map((opp) => {
            const deadline = formatDeadline(opp.response_deadline);
            const orgBadge = getOrgTypeBadge(opp.organization_type);
            
            return (
              <Card 
                key={opp.id} 
                className="hover:border-primary/50 transition-all cursor-pointer"
                onClick={() => openDetail(opp)}
                data-testid={`opportunity-card-${opp.id}`}
              >
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        {opp.is_featured && (
                          <Star className="h-4 w-4 text-yellow-500 fill-yellow-500 flex-shrink-0" />
                        )}
                        <h3 className="font-medium line-clamp-2">{opp.title}</h3>
                      </div>
                      <p className="text-sm text-muted-foreground flex items-center gap-1">
                        <Building2 className="h-3 w-3 flex-shrink-0" />
                        <span className="truncate">{opp.organization_name || "Unknown"}</span>
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="flex-shrink-0"
                      onClick={(e) => { e.stopPropagation(); handleSave(opp); }}
                      disabled={saving}
                    >
                      {opp.saved ? (
                        <BookmarkCheck className="h-5 w-5 text-primary" />
                      ) : (
                        <Bookmark className="h-5 w-5" />
                      )}
                    </Button>
                  </div>

                  <p className="text-sm text-muted-foreground line-clamp-2">
                    {opp.description}
                  </p>

                  <div className="flex flex-wrap gap-2">
                    <Badge variant="outline" className={orgBadge.color}>
                      {orgBadge.label}
                    </Badge>
                    {opp.category && (
                      <Badge variant="outline" className="capitalize">
                        {opp.category.replace("_", " ")}
                      </Badge>
                    )}
                    {opp.source_type === "sam_gov" && (
                      <Badge variant="outline" className="bg-blue-500/15 text-blue-300">
                        SAM.gov
                      </Badge>
                    )}
                    {(opp.sam_notice_type_label || opp.solicitation_type) && (
                      <Badge variant="outline">
                        {opp.sam_notice_type_label || opp.solicitation_type}
                      </Badge>
                    )}
                    {opp.match_score !== undefined && (
                      <Badge variant="outline" className="bg-green-500/15 text-green-300">
                        {opp.match_score}% match
                      </Badge>
                    )}
                    {opp.set_aside_type && (
                      <Badge variant="outline" className="bg-cyan-500/20 text-cyan-400">
                        {SET_ASIDES.find(s => s.value === opp.set_aside_type)?.label || opp.set_aside_type}
                      </Badge>
                    )}
                  </div>

                  <div className="flex items-center justify-between text-sm pt-2 border-t border-border/50">
                    <div className="flex items-center gap-1 text-muted-foreground">
                      <MapPin className="h-3 w-3" />
                      {opp.city && opp.state ? `${opp.city}, ${opp.state}` : opp.state || "N/A"}
                    </div>
                    <div className="font-medium text-primary">
                      {formatCurrency(opp.estimated_value_min, opp.estimated_value_max)}
                    </div>
                  </div>

                  {deadline && (
                    <div className={`flex items-center gap-1 text-sm ${deadline.urgent ? "text-red-400" : "text-muted-foreground"}`}>
                      <Calendar className="h-3 w-3" />
                      Due: {deadline.text}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      ))}

      {/* Pagination */}
      {(activeTab === "search" || activeTab === "recommended") && totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm text-muted-foreground">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Preference Profile Dialog */}
      <Dialog open={showProfileDialog} onOpenChange={setShowProfileDialog}>
        <DialogContent className="bg-card max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingProfile ? "Edit Preference Profile" : "New Preference Profile"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
            <div className="space-y-2 md:col-span-2">
              <Label>Name</Label>
              <Input value={profileForm.name} onChange={(e) => setProfileForm({...profileForm, name: e.target.value})} placeholder="Federal concrete jobs near Knoxville" />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Description</Label>
              <Textarea value={profileForm.description} onChange={(e) => setProfileForm({...profileForm, description: e.target.value})} placeholder="What this profile is intended to find" />
            </div>
            <div className="space-y-2">
              <Label>Keywords</Label>
              <Input value={profileForm.keywords} onChange={(e) => setProfileForm({...profileForm, keywords: e.target.value})} placeholder="concrete, sitework, paving" />
            </div>
            <div className="space-y-2">
              <Label>Negative Keywords</Label>
              <Input value={profileForm.negative_keywords} onChange={(e) => setProfileForm({...profileForm, negative_keywords: e.target.value})} placeholder="janitorial, food service" />
            </div>
            <div className="space-y-2">
              <Label>NAICS Codes</Label>
              <Input value={profileForm.naics_codes} onChange={(e) => setProfileForm({...profileForm, naics_codes: e.target.value})} placeholder="236220, 238110" />
            </div>
            <div className="space-y-2">
              <Label>PSC Codes</Label>
              <Input value={profileForm.psc_codes} onChange={(e) => setProfileForm({...profileForm, psc_codes: e.target.value})} placeholder="Y1AA, Z2AA" />
            </div>
            <div className="space-y-2">
              <Label>Notice Types</Label>
              <Input value={profileForm.notice_types} onChange={(e) => setProfileForm({...profileForm, notice_types: e.target.value})} placeholder="r, p, o, k" />
            </div>
            <div className="space-y-2">
              <Label>Set-Asides</Label>
              <Input value={profileForm.set_aside_types} onChange={(e) => setProfileForm({...profileForm, set_aside_types: e.target.value})} placeholder="small_business, sdvosb" />
            </div>
            <div className="space-y-2">
              <Label>States</Label>
              <Input value={profileForm.states} onChange={(e) => setProfileForm({...profileForm, states: e.target.value})} placeholder="TN, KY, GA" />
            </div>
            <div className="space-y-2">
              <Label>ZIP Codes</Label>
              <Input value={profileForm.zip_codes} onChange={(e) => setProfileForm({...profileForm, zip_codes: e.target.value})} placeholder="37902, 37201" />
            </div>
            <div className="space-y-2">
              <Label>Agencies</Label>
              <Input value={profileForm.agency_names} onChange={(e) => setProfileForm({...profileForm, agency_names: e.target.value})} placeholder="Department of Veterans Affairs, USACE" />
            </div>
            <div className="space-y-2">
              <Label>Radius Miles</Label>
              <Input type="number" value={profileForm.radius_miles} onChange={(e) => setProfileForm({...profileForm, radius_miles: e.target.value})} placeholder="150" />
            </div>
            <div className="space-y-2">
              <Label>Min Response Days</Label>
              <Input type="number" value={profileForm.min_response_days} onChange={(e) => setProfileForm({...profileForm, min_response_days: e.target.value})} placeholder="7" />
            </div>
            <div className="space-y-2">
              <Label>Max Response Days</Label>
              <Input type="number" value={profileForm.max_response_days} onChange={(e) => setProfileForm({...profileForm, max_response_days: e.target.value})} placeholder="90" />
            </div>
            <div className="space-y-2">
              <Label>Min Value</Label>
              <Input type="number" value={profileForm.min_estimated_value} onChange={(e) => setProfileForm({...profileForm, min_estimated_value: e.target.value})} placeholder="50000" />
            </div>
            <div className="space-y-2">
              <Label>Max Value</Label>
              <Input type="number" value={profileForm.max_estimated_value} onChange={(e) => setProfileForm({...profileForm, max_estimated_value: e.target.value})} placeholder="5000000" />
            </div>
            <div className="space-y-2">
              <Label>Risk Tolerance</Label>
              <Select value={profileForm.risk_tolerance} onValueChange={(v) => setProfileForm({...profileForm, risk_tolerance: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2 pt-7">
              <input type="checkbox" checked={profileForm.is_default} onChange={(e) => setProfileForm({...profileForm, is_default: e.target.checked})} />
              <Label>Default profile</Label>
              <input className="ml-4" type="checkbox" checked={profileForm.enabled} onChange={(e) => setProfileForm({...profileForm, enabled: e.target.checked})} />
              <Label>Enabled</Label>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowProfileDialog(false)}>Cancel</Button>
            <Button onClick={saveProfile}>{editingProfile ? "Save Changes" : "Create Profile"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="bg-card max-w-3xl max-h-[85vh] overflow-y-auto">
          {selectedOpp && (
            <>
              <DialogHeader>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      {selectedOpp.is_featured && (
                        <Star className="h-5 w-5 text-yellow-500 fill-yellow-500" />
                      )}
                      <DialogTitle className="text-xl">{selectedOpp.title}</DialogTitle>
                    </div>
                    <p className="text-muted-foreground flex items-center gap-2">
                      <Building2 className="h-4 w-4" />
                      {selectedOpp.organization_name}
                      {selectedOpp.organization_department && ` - ${selectedOpp.organization_department}`}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2 justify-end">
                    <Button
                      variant={selectedOpp.saved ? "default" : "outline"}
                      onClick={() => handleSave(selectedOpp)}
                      disabled={saving}
                    >
                      {selectedOpp.saved ? (
                        <>
                          <BookmarkCheck className="h-4 w-4 mr-2" />
                          Saved
                        </>
                      ) : (
                        <>
                          <Bookmark className="h-4 w-4 mr-2" />
                          Save
                        </>
                      )}
                    </Button>
                    <Button variant="outline" onClick={() => setPursuitStatus("reviewing")}>Review</Button>
                    <Button variant="outline" onClick={() => setPursuitStatus("pursuing")}>Pursue</Button>
                    <Button variant="outline" onClick={() => setPursuitStatus("not_pursuing")}>Pass</Button>
                    <Button onClick={convertToCrmOpportunity}>Convert</Button>
                  </div>
                </div>
              </DialogHeader>

              <div className="space-y-6 py-4">
                {/* Pursuit Status */}
                {selectedOpp.saved_info && (
                  <div className="p-4 rounded-lg bg-primary/10 border border-primary/20">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <h4 className="font-medium">Pursuit Status</h4>
                        <p className="text-sm text-muted-foreground">
                          {selectedOpp.saved_info.application_status || "saved"}
                          {selectedOpp.saved_info.priority && ` · ${selectedOpp.saved_info.priority} priority`}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => setPursuitStatus("preparing")}>Preparing</Button>
                        <Button size="sm" variant="outline" onClick={() => setPursuitStatus("submitted")}>Submitted</Button>
                        <Button size="sm" variant="outline" onClick={() => setPursuitStatus("awarded")}>Won</Button>
                        <Button size="sm" variant="outline" onClick={() => setPursuitStatus("lost")}>Lost</Button>
                      </div>
                    </div>
                    {selectedOpp.saved_info.notes && <p className="text-sm mt-2 whitespace-pre-wrap">{selectedOpp.saved_info.notes}</p>}
                  </div>
                )}

                {/* Key Info Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-3 rounded-lg bg-muted/30">
                    <p className="text-xs text-muted-foreground mb-1">Est. Value</p>
                    <p className="font-medium text-primary">
                      {formatCurrency(selectedOpp.estimated_value_min, selectedOpp.estimated_value_max)}
                    </p>
                  </div>
                  <div className="p-3 rounded-lg bg-muted/30">
                    <p className="text-xs text-muted-foreground mb-1">Response Due</p>
                    <p className={`font-medium ${formatDeadline(selectedOpp.response_deadline)?.urgent ? "text-red-400" : ""}`}>
                      {selectedOpp.response_deadline 
                        ? new Date(selectedOpp.response_deadline).toLocaleDateString()
                        : "Not specified"
                      }
                    </p>
                  </div>
                  <div className="p-3 rounded-lg bg-muted/30">
                    <p className="text-xs text-muted-foreground mb-1">Location</p>
                    <p className="font-medium">
                      {selectedOpp.city && selectedOpp.state 
                        ? `${selectedOpp.city}, ${selectedOpp.state}` 
                        : selectedOpp.state || "N/A"
                      }
                    </p>
                  </div>
                  <div className="p-3 rounded-lg bg-muted/30">
                    <p className="text-xs text-muted-foreground mb-1">Solicitation #</p>
                    <p className="font-medium text-sm">{selectedOpp.solicitation_number || "N/A"}</p>
                  </div>
                </div>

                {/* Badges */}
                <div className="flex flex-wrap gap-2">
                  <Badge variant="outline" className={getOrgTypeBadge(selectedOpp.organization_type).color}>
                    {getOrgTypeBadge(selectedOpp.organization_type).label}
                  </Badge>
                  {selectedOpp.category && (
                    <Badge variant="outline" className="capitalize">
                      {selectedOpp.category.replace("_", " ")}
                    </Badge>
                  )}
                  {selectedOpp.source_type === "sam_gov" && (
                    <Badge variant="outline" className="bg-blue-500/15 text-blue-300">
                      SAM.gov
                    </Badge>
                  )}
                  {(selectedOpp.sam_notice_type_label || selectedOpp.solicitation_type) && (
                    <Badge variant="outline" className="uppercase">
                      {selectedOpp.sam_notice_type_label || selectedOpp.solicitation_type}
                    </Badge>
                  )}
                  {selectedOpp.set_aside_type && (
                    <Badge variant="outline" className="bg-cyan-500/20 text-cyan-400">
                      {SET_ASIDES.find(s => s.value === selectedOpp.set_aside_type)?.label || selectedOpp.set_aside_type}
                    </Badge>
                  )}
                </div>

                {/* SAM.gov Metadata */}
                {selectedOpp.source_type === "sam_gov" && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="p-3 rounded-lg bg-muted/30">
                      <p className="text-xs text-muted-foreground mb-1">NAICS</p>
                      <p className="font-medium text-sm">{selectedOpp.naics_codes?.join(", ") || "N/A"}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-muted/30">
                      <p className="text-xs text-muted-foreground mb-1">PSC</p>
                      <p className="font-medium text-sm">{selectedOpp.psc_codes?.join(", ") || "N/A"}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-muted/30">
                      <p className="text-xs text-muted-foreground mb-1">Last Synced</p>
                      <p className="font-medium text-sm">
                        {selectedOpp.last_synced_at ? new Date(selectedOpp.last_synced_at).toLocaleString() : "N/A"}
                      </p>
                    </div>
                  </div>
                )}

                {/* Match Reasons */}
                {selectedOpp.match_score !== undefined && (
                  <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/20">
                    <h4 className="font-medium mb-2">Match Score: {selectedOpp.match_score}%</h4>
                    {selectedOpp.intelligence?.recommendation && (
                      <p className="text-sm text-green-200 mb-2">{selectedOpp.intelligence.recommendation}</p>
                    )}
                    {selectedOpp.intelligence?.summary && (
                      <p className="text-sm text-muted-foreground mb-3">{selectedOpp.intelligence.summary}</p>
                    )}
                    <div className="grid grid-cols-3 gap-2 text-sm mb-3">
                      <span>Fit: {selectedOpp.fit_score || 0}</span>
                      <span>Urgency: {selectedOpp.urgency_score || 0}</span>
                      <span>Freshness: {selectedOpp.freshness_score || 0}</span>
                    </div>
                    {(selectedOpp.match_reasons || []).length > 0 && (
                      <ul className="list-disc pl-5 text-sm text-muted-foreground">
                        {selectedOpp.match_reasons.map((r, i) => <li key={i}>{r}</li>)}
                      </ul>
                    )}
                    {(selectedOpp.disqualifiers || []).length > 0 && (
                      <div className="mt-3 text-sm text-red-300">Red flags: {selectedOpp.disqualifiers.join('; ')}</div>
                    )}
                  </div>
                )}

                {/* Description */}
                <div>
                  <h4 className="font-medium mb-2">Description</h4>
                  <p className="text-muted-foreground whitespace-pre-wrap">
                    {selectedOpp.description || "No description provided."}
                  </p>
                </div>

                {/* Scope of Work */}
                {selectedOpp.scope_of_work && (
                  <div>
                    <h4 className="font-medium mb-2">Scope of Work</h4>
                    <p className="text-muted-foreground whitespace-pre-wrap">
                      {selectedOpp.scope_of_work}
                    </p>
                  </div>
                )}

                {/* Requirements */}
                {(selectedOpp.certifications_required?.length > 0 || selectedOpp.bonding_required) && (
                  <div>
                    <h4 className="font-medium mb-2">Requirements</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedOpp.certifications_required?.map(cert => (
                        <Badge key={cert} variant="outline" className="bg-amber-500/20 text-amber-400">
                          <Shield className="h-3 w-3 mr-1" />
                          {cert}
                        </Badge>
                      ))}
                      {selectedOpp.bonding_required && (
                        <Badge variant="outline" className="bg-purple-500/20 text-purple-400">
                          Bonding Required
                          {selectedOpp.bonding_amount && `: $${(selectedOpp.bonding_amount / 1000000).toFixed(1)}M`}
                        </Badge>
                      )}
                    </div>
                  </div>
                )}

                {/* Contact */}
                {(selectedOpp.contact_name || selectedOpp.contact_email) && (
                  <div>
                    <h4 className="font-medium mb-2">Contact</h4>
                    <div className="text-muted-foreground">
                      {selectedOpp.contact_name && <p>{selectedOpp.contact_name}</p>}
                      {selectedOpp.contact_email && (
                        <a href={`mailto:${selectedOpp.contact_email}`} className="text-primary hover:underline">
                          {selectedOpp.contact_email}
                        </a>
                      )}
                      {selectedOpp.contact_phone && <p>{selectedOpp.contact_phone}</p>}
                    </div>
                  </div>
                )}

                {/* Source Link */}
                {selectedOpp.source_url && (
                  <div className="pt-4 border-t">
                    <Button variant="outline" asChild>
                      <a href={selectedOpp.source_url} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="h-4 w-4 mr-2" />
                        View Original Posting
                      </a>
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DiscoverPage;
